import { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUser } from '../App'
import { getItens, criarComanda, criarLancamento, getMesaBalcao } from '../api/client'
import Icon from './Icon'

/**
 * BalcaoPDV — painel de venda avulsa integrado na MesasPage (D-114, 2026-05-17).
 *
 * Diferente do `BalcaoQuickModal` antigo (que era 1 item por vez), este suporta
 * carrinho de N itens, do estilo PDV de frente de caixa:
 *   - Busca + tabs de categoria
 *   - Toca item → adiciona ao carrinho (qtd + total)
 *   - Botões + / - / × por linha do carrinho
 *   - Total grande embaixo
 *   - [Cobrar] → cria comanda Balcão + lança todos itens + abre fechamento
 *
 * Identidade Myro's: cabeçalho com label "Balcão", terracota nos acentos,
 * sem clone de PDV genérico. Pra mobile o componente pai (MesasPage) usa
 * tabs em vez de split.
 */

// D-144 (2026-05-19): TODAS as categorias do cardápio disponíveis no Balcão.
// Ordem segue o cardápio cliente: marmitas/adicionais primeiro, depois bebidas
// (bebidas em ordem de consumo típico), depois doces/extras.
const CATS_RAPIDAS: Array<{ key: string; label: string }> = [
  { key: 'marmitas', label: 'Marmitas' },
  { key: 'adicionais', label: 'Adicionais' },
  { key: 'aguas', label: 'Águas' },
  { key: 'refrigerantes_lata', label: 'Refri lata' },
  { key: 'refrigerantes_garrafa', label: 'Refri garrafa' },
  { key: 'cervejas', label: 'Cervejas' },
  { key: 'sucos_naturais', label: 'Sucos' },
  { key: 'cremes', label: 'Cremes' },
  { key: 'combinacoes_sucos', label: 'Combinações' },
  { key: 'complementos_rapidos', label: 'Complementos' },
  { key: 'doces_caixa', label: 'Doces' },
  { key: 'extras', label: 'Extras' },
]

interface CartLine {
  id: number
  nome: string
  preco: number
  qtd: number
}

export default function BalcaoPDV() {
  const { user } = useUser()
  const nav = useNavigate()
  const [itens, setItens] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busca, setBusca] = useState('')
  const [catSelecionada, setCatSelecionada] = useState<string>('marmitas')
  const [carrinho, setCarrinho] = useState<CartLine[]>([])
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancel = false
    setLoading(true)
    getItens()
      .then(arr => { if (!cancel) { setItens(arr); setLoading(false) } })
      .catch(e => { if (!cancel) { setError(e?.message || 'Erro ao carregar itens'); setLoading(false) } })
    return () => { cancel = true }
  }, [])

  const itensFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase()
    if (q) return itens.filter(i => i.ativo && String(i.nome || '').toLowerCase().includes(q)).slice(0, 30)
    return itens.filter(i => i.ativo && i.categoria === catSelecionada)
  }, [itens, busca, catSelecionada])

  const total = useMemo(
    () => carrinho.reduce((s, c) => s + c.qtd * c.preco, 0),
    [carrinho],
  )

  function addItem(item: any) {
    setCarrinho(c => {
      const idx = c.findIndex(l => l.id === item.id)
      if (idx >= 0) {
        const novo = [...c]
        novo[idx] = { ...novo[idx], qtd: novo[idx].qtd + 1 }
        return novo
      }
      return [...c, { id: item.id, nome: item.nome, preco: Number(item.preco), qtd: 1 }]
    })
  }

  function changeQtd(id: number, delta: number) {
    setCarrinho(c => c
      .map(l => l.id === id ? { ...l, qtd: l.qtd + delta } : l)
      .filter(l => l.qtd > 0)
    )
  }

  function removeItem(id: number) {
    setCarrinho(c => c.filter(l => l.id !== id))
  }

  function limpar() {
    setCarrinho([])
  }

  async function cobrar() {
    if (carrinho.length === 0 || submitting) return
    setSubmitting(true)
    setError('')
    try {
      const balcao = await getMesaBalcao()
      const c = await criarComanda({
        mesa_id: balcao.id,
        origem_inicial: 'vazia',
        criada_por: user.id,
        nome_cliente_opcional: 'Balcão',
      })
      // Lança N itens em série (mantém ordem)
      for (const l of carrinho) {
        await criarLancamento({
          comanda_id: c.id,
          mesa_id: balcao.id,
          item_id: l.id,
          descricao: l.nome,
          quantidade: l.qtd,
          valor_unitario: l.preco,
          valor_total: l.preco * l.qtd,
          origem: 'manual_item',
          criado_por: user.id,
        })
      }
      setCarrinho([])
      nav(`/fechamento/${balcao.id}`)
    } catch (e: any) {
      setError(e?.message || 'Erro ao cobrar Balcão')
      setSubmitting(false)
    }
  }

  const fmtBRL = (v: number) => `R$ ${v.toFixed(2).replace('.', ',')}`

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-branco-gelo via-white to-cream/30 border-l border-cream-border">
      {/* Header — D-122 refinado com gradiente sutil terroso */}
      <div className="px-4 py-3 border-b border-cream-border bg-gradient-to-r from-vinho/[0.04] via-terracota/[0.03] to-transparent">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-vinho/70 font-medium">PDV · Frente de caixa</p>
            <h2 className="font-serif text-lg font-semibold text-vinho leading-tight">Balcão Myro's</h2>
          </div>
          {carrinho.length > 0 && (
            <button
              onClick={limpar}
              className="text-[11px] text-gray-500 hover:text-red-600 px-2 py-1 rounded"
              title="Limpar carrinho"
            >
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Busca + Tabs */}
      <div className="px-4 pt-3 pb-2 border-b border-gray-100">
        <div className="relative mb-2">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Icon name="search" size={14} />
          </span>
          <input
            type="text"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Buscar item…"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:border-terracota focus:outline-none"
          />
        </div>
        {!busca.trim() && (
          // D-146 #2: wrap em 2 linhas (mais escaneável) ao invés de scroll horizontal.
          // 13 categorias caem bem em 3 linhas no mobile, 2 no desktop.
          <div className="flex flex-wrap gap-1.5">
            {CATS_RAPIDAS.map(c => (
              <button
                key={c.key}
                onClick={() => setCatSelecionada(c.key)}
                className={`whitespace-nowrap px-2.5 py-1 rounded-full text-[11px] font-medium transition border ${
                  catSelecionada === c.key
                    ? 'bg-terracota text-white border-terracota'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-terracota/40'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lista de itens */}
      <div className="flex-1 overflow-y-auto px-3 py-2 min-h-0">
        {loading ? (
          <p className="text-center text-gray-400 py-6 text-xs">Carregando…</p>
        ) : itensFiltrados.length === 0 ? (
          <p className="text-center text-gray-400 py-6 text-xs">Nenhum item.</p>
        ) : (
          <div className="grid grid-cols-2 gap-1.5">
            {itensFiltrados.map(item => (
              <button
                key={item.id}
                onClick={() => addItem(item)}
                disabled={submitting}
                className="border border-gray-200 bg-white rounded-lg p-2 text-left shadow-sm hover:border-terracota/40 hover:shadow transition-all flex flex-col gap-0.5 min-h-[60px] disabled:opacity-50"
              >
                <p className="font-medium text-[12px] text-carvao leading-tight line-clamp-2">{item.nome}</p>
                <p className="font-mono text-[11px] text-terracota tabular-nums mt-auto">
                  {fmtBRL(Number(item.preco))}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Carrinho */}
      <div className="border-t border-cream-border bg-cream/30 shrink-0">
        {error && (
          <div className="px-3 py-2 text-xs text-red-700 bg-red-50 border-b border-red-100">{error}</div>
        )}
        <div className="px-3 py-2 max-h-[28vh] overflow-y-auto">
          {carrinho.length === 0 ? (
            // D-146 #3: estado vazio mais útil — mostra atalhos rápidos pros favoritos padrão
            // do salão (já marcados como `favorito_padrao=true` no cadastro de itens).
            <div>
              <p className="text-center text-gray-400 py-1 text-[11px]">Toque um item pra começar</p>
              {(() => {
                const favs = itens.filter(i => i.ativo && i.favorito_padrao).slice(0, 4)
                if (favs.length === 0) return null
                return (
                  <div className="mt-1">
                    <p className="text-[9px] uppercase tracking-wider text-gray-400 mb-1 font-semibold">
                      Atalhos rápidos
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {favs.map(item => (
                        <button
                          key={item.id}
                          onClick={() => {
                            setCarrinho(prev => {
                              const ex = prev.find(l => l.id === item.id)
                              if (ex) return prev.map(l => l.id === item.id ? { ...l, qtd: l.qtd + 1 } : l)
                              return [...prev, { id: item.id, nome: item.nome, preco: Number(item.preco), qtd: 1 }]
                            })
                          }}
                          className="text-[10px] px-2 py-1 rounded-md bg-white border border-cream-border hover:border-terracota/40 text-carvao transition"
                        >
                          + {item.nome}
                          <span className="ml-1 text-terracota font-mono">{fmtBRL(Math.round(Number(item.preco) * 100))}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </div>
          ) : (
            <div className="space-y-1.5">
              {carrinho.map(l => (
                <div key={l.id} className="flex items-center gap-1.5 text-xs">
                  <button
                    onClick={() => changeQtd(l.id, -1)}
                    className="w-6 h-6 flex items-center justify-center rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold"
                  >
                    −
                  </button>
                  <span className="font-mono tabular-nums w-6 text-center font-semibold">{l.qtd}</span>
                  <button
                    onClick={() => changeQtd(l.id, +1)}
                    className="w-6 h-6 flex items-center justify-center rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 font-bold"
                  >
                    +
                  </button>
                  <span className="flex-1 truncate text-carvao">{l.nome}</span>
                  <span className="font-mono tabular-nums text-terracota text-[12px]">
                    {fmtBRL(l.qtd * l.preco)}
                  </span>
                  <button
                    onClick={() => removeItem(l.id)}
                    className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-600"
                    title="Remover"
                  >
                    <Icon name="close" size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Total + Cobrar */}
        <div className="px-3 py-3 border-t border-cream-border">
          <div className="flex items-baseline justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Total</span>
            <span className="font-mono text-2xl font-bold tabular-nums text-terracota">
              {fmtBRL(total)}
            </span>
          </div>
          <button
            onClick={cobrar}
            disabled={carrinho.length === 0 || submitting}
            className="w-full bg-terracota text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-terracota-dark transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Processando…' : carrinho.length === 0 ? 'Carrinho vazio' : `Cobrar ${fmtBRL(total)}`}
          </button>
        </div>
      </div>
    </div>
  )
}
